export const FRONTEND_RELEASE_IDLE_TIMEOUT_MS = 5 * 60 * 1000;
export const FRONTEND_RELEASE_RECONNECT_NOTICE_DELAY_MS = 3000;
export const FRONTEND_RELEASE_RECONNECT_DELAYS_MS = [1000, 2000, 3000, 5000] as const;
export const FRONTEND_RELEASE_SSE_PATH = '/api/sse/frontend-release';

const BUILD_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;
const DISMISS_STORAGE_PREFIX = 'sus:frontend-release-dismissed:v1:';

export type FrontendReleaseEnvelope =
  | {
      type: 'release';
      buildId: string;
      timestamp: string;
    }
  | {
      type: 'heartbeat';
      timestamp: string;
    };

/** 외부에서 전달된 Next.js BUILD_ID를 허용 형식으로 정규화합니다. */
export function normalizeFrontendBuildId(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalizedValue = value.trim();
  return BUILD_ID_PATTERN.test(normalizedValue) ? normalizedValue : null;
}

/** 배포 버전 SSE payload를 검증된 클라이언트 envelope로 변환합니다. */
export function parseFrontendReleaseEnvelope(value: unknown): FrontendReleaseEnvelope | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Record<string, unknown>;
  if (record.type === 'heartbeat' && typeof record.timestamp === 'string' && Boolean(record.timestamp.trim())) {
    return {
      type: 'heartbeat',
      timestamp: record.timestamp,
    };
  }

  const buildId = normalizeFrontendBuildId(record.buildId);
  if (record.type !== 'release' || !buildId || typeof record.timestamp !== 'string' || !record.timestamp.trim()) {
    return null;
  }

  return {
    type: 'release',
    buildId,
    timestamp: record.timestamp,
  };
}

/** 마지막 사용자 활동 시각을 기준으로 자동 갱신 가능 여부를 판정합니다. */
export function shouldReloadFrontendRelease(lastActivityAt: number, now: number, idleTimeoutMs = FRONTEND_RELEASE_IDLE_TIMEOUT_MS): boolean {
  if (!Number.isFinite(lastActivityAt) || !Number.isFinite(now) || !Number.isFinite(idleTimeoutMs) || idleTimeoutMs < 0) {
    return false;
  }

  return now - lastActivityAt >= idleTimeoutMs;
}

/** 대상 BUILD_ID별 배포 안내 닫힘 상태를 저장할 key를 생성합니다. */
export function buildFrontendReleaseDismissStorageKey(buildId: unknown): string | null {
  const normalizedBuildId = normalizeFrontendBuildId(buildId);
  return normalizedBuildId ? `${DISMISS_STORAGE_PREFIX}${normalizedBuildId}` : null;
}

/** 재연결 횟수에 따라 상한이 있는 지연 시간을 반환합니다. */
export function resolveFrontendReleaseReconnectDelayMs(attempt: number): number {
  if (!Number.isFinite(attempt) || attempt <= 0) {
    return FRONTEND_RELEASE_RECONNECT_DELAYS_MS[0];
  }

  const index = Math.min(Math.floor(attempt), FRONTEND_RELEASE_RECONNECT_DELAYS_MS.length - 1);
  return FRONTEND_RELEASE_RECONNECT_DELAYS_MS[index];
}
